'use strict';

import { sharedStorage } from '../utils/sharedStorage';
import { tabIdentity } from '../utils/tabIdentity';

const saveToCxpGate = require('../product/features/saveToCxpGate');

/**
 * Save Execution Gate — exclusive short lease for CXP configuration writes (ADR 0004).
 *
 * **Purpose:** Ensure only one storefront tab at a time holds a renewing
 * `localStorage` lease while saving/updating a build to CXP, and mirror that
 * lock onto peer-tab CTAs.
 *
 * **Scope:**
 * - Lease claim / renew / release (`tryClaimSaveGate`, `releaseSaveGate`, timers)
 * - Peer CTA block/unblock from the shared slot (`syncSaveGateFromSlot`,
 *   `applySaveGatePeerBlock`, `SAVE_GATE_CTA_SELECTOR`)
 * - Local CXP write orchestration for configurator CTAs
 *   (`beginSaveGateAction` / `completeSaveGateAction`)
 *
 * **Out of scope:** Peer bus / DOM wiring (`product/detail.registerSaveExecutionGate`);
 * AJAX payloads / CXP business success (configurator); Checkout Ownership /
 * Order Now; CSRF; title attention. Global presence/attention/reconcile topics
 * → `components/coordinator`.
 */
export const saveExecutionGate = {
  /**
   * @readonly
   * @private
   */
  STATE_KEY: 'saveGate',

  /**
   * @readonly
   * @private
   */
  SAVE_GATE_KEY: 'honda_multi_cart_save_gate',

  /**
   * @readonly
   * @private
   */
  SAVE_GATE_LEASE_MS: 3000,

  /**
   * @readonly
   * @private
   */
  SAVE_GATE_RENEW_MS: 1000,

  /**
   * @readonly
   * @private
   */
  SAVE_GATE_CTA_SELECTOR: [
    '.configurator-send-offine-order-cta',
    '.modify-car-summary-update-order-cta',
    '#modify-car-update-button',
    '#configurator-save-build-cta',
    '.btn-save-to-cxp',
    '.btn-update-order'
  ].join(', '),

  /**
   * @readonly
   * @private
   */
  SAVE_GATE_PEER_ATTR: 'data-mc-save-gate',

  /**
   * @private
   *
   * @returns {{
   *   initialized: boolean,
   *   held: boolean,
   *   renewTimer: number|null,
   *   watchTimer: number|null
   * }}
   */
  get state() {
    return sharedStorage.get(this.STATE_KEY, () => ({
      initialized: false,
      held: false,
      renewTimer: null,
      watchTimer: null
    }));
  },

  /**
   * Whether peer bus + DOM listeners for this module have been registered.
   *
   * @returns {boolean}
   */
  isInitialized() {
    return !!this.state.initialized;
  },

  /**
   * Mark peer bus + DOM listeners as registered (idempotent wirer guard).
   */
  setInitialized() {
    this.state.initialized = true;
  },

  /**
   * Whether this tab currently holds the Save Execution Gate lease.
   *
   * @returns {boolean}
   */
  isHeld() {
    return !!this.state.held;
  },

  /**
   * Claim the gate and disable local CTA(s) for a CXP write.
   * Returns false when a CTA is already disabled or a peer holds the lease.
   *
   * @param {string|Element|JQuery} cta - CTA(s) to disable for this write
   * @param {string|Element|JQuery} [alsoRequireEnabled] - extra elements that must be enabled
   *
   * @returns {boolean}
   */
  beginSaveGateAction(cta, alsoRequireEnabled) {
    const $ctas = $(cta);
    const $guards = alsoRequireEnabled
      ? $(alsoRequireEnabled).add($ctas)
      : $ctas;

    if ($guards.length && this.isAnySaveGateCtaDisabled($guards)) {
      return false;
    }

    if (!this.tryClaimSaveGate()) {
      return false;
    }

    if ($ctas.length) {
      this.setLocalSaveGateCtaDisabled($ctas, true);
    }

    return true;
  },

  /**
   * Release the gate and restore CTA(s) unless peer-blocked or terminal success.
   *
   * @param {string|Element|JQuery} cta
   */
  completeSaveGateAction(cta) {
    this.releaseSaveGate();

    const $ctas = $(cta);

    if (!$ctas.length) {
      return;
    }

    this.restoreLocalSaveGateCtas($ctas);
  },

  /**
   * Peer handler for `save:lock` — re-sync CTA block from the shared slot.
   *
   * @param {{ tabId?: string }} [payload]
   */
  onSaveLock(payload = {}) {
    this.syncSaveGateFromSlot();
  },

  /**
   * Peer handler for `save:renew` — re-sync CTA block from the shared slot.
   *
   * @param {{ tabId?: string }} [payload]
   */
  onSaveRenew(payload = {}) {
    this.syncSaveGateFromSlot();
  },

  /**
   * Peer handler for `save:unlock` — re-sync CTA block from the shared slot.
   *
   * @param {{ tabId?: string }} [payload]
   */
  onSaveUnlock(payload = {}) {
    this.syncSaveGateFromSlot();
  },

  /**
   * storage listener for save-gate slot changes.
   *
   * @param {StorageEvent} event
   */
  onSaveGateStorage(event) {
    if (event.key !== this.SAVE_GATE_KEY) {
      return;
    }

    this.syncSaveGateFromSlot();
  },

  /**
   * Release immediately (complete / error / unload). Idempotent.
   */
  releaseSaveGate() {
    const state = this.state;

    this.stopSaveGateRenew();

    const tabId = tabIdentity.peekTabId();
    const wasHeld = state.held;

    state.held = false;

    try {
      if (tabId) {
        const raw = localStorage.getItem(this.SAVE_GATE_KEY);

        if (raw) {
          const entry = JSON.parse(raw);

          if (entry && entry.tabId === tabId) {
            localStorage.removeItem(this.SAVE_GATE_KEY);
          }
        }
      }
    } catch (e) {
      // Best-effort clear.
    }

    if (wasHeld && tabId) {
      sharedStorage.tabChannel.publish('save:unlock', { tabId: tabId });
    }

    this.syncSaveGateFromSlot();
  },

  /**
   * Re-read the slot and disable/enable peer CXP CTAs. Schedules unlock when
   * the foreign lease expires without an unlock message.
   */
  syncSaveGateFromSlot() {
    const state = this.state;

    if (state.watchTimer) {
      window.clearTimeout(state.watchTimer);
      state.watchTimer = null;
    }

    const gate = this.getSaveGate();
    const selfTabId = tabIdentity.peekTabId();
    const blocked = !!(gate && selfTabId && gate.tabId !== selfTabId);

    this.applySaveGatePeerBlock(blocked);

    if (blocked) {
      const delay = Math.max(0, gate.until - Date.now()) + 50;

      state.watchTimer = window.setTimeout(() => {
        state.watchTimer = null;
        this.syncSaveGateFromSlot();
      }, delay);
    }
  },

  /**
   * Active Save Execution Gate holder, or null if empty/expired.
   *
   * @private
   *
   * @returns {{ tabId: string, until: number, nonce: string }|null}
   */
  getSaveGate() {
    try {
      const raw = localStorage.getItem(this.SAVE_GATE_KEY);

      if (!raw) {
        return null;
      }

      const entry = JSON.parse(raw);

      if (!entry || !entry.tabId || !(entry.until > Date.now())) {
        localStorage.removeItem(this.SAVE_GATE_KEY);
        return null;
      }

      return {
        tabId: entry.tabId,
        until: entry.until,
        nonce: entry.nonce || ''
      };
    } catch (e) {
      return null;
    }
  },

  /**
   * Claim the Save Execution Gate before a CXP write. Fail-open on storage/
   * missing tabId. Returns false only when another tab clearly holds the lease.
   *
   * @private
   *
   * @returns {boolean}
   */
  tryClaimSaveGate() {
    const state = this.state;

    if (state.held) {
      return false;
    }

    const tabId = tabIdentity.peekTabId();

    if (!tabId) {
      return true;
    }

    const existing = this.getSaveGate();

    if (existing && existing.tabId !== tabId) {
      this.syncSaveGateFromSlot();
      return false;
    }

    if (!this.writeSaveGateSlot(tabId)) {
      return true;
    }

    state.held = true;
    this.startSaveGateRenew();
    sharedStorage.tabChannel.publish('save:lock', { tabId: tabId });
    this.syncSaveGateFromSlot();
    return true;
  },

  /**
   * @private
   *
   * @param {string} tabId
   *
   * @returns {boolean} false when storage write failed
   */
  writeSaveGateSlot(tabId) {
    try {
      localStorage.setItem(this.SAVE_GATE_KEY, JSON.stringify({
        tabId: tabId,
        until: Date.now() + this.SAVE_GATE_LEASE_MS,
        nonce: Math.random().toString(36).slice(2)
      }));
      return true;
    } catch (e) {
      return false;
    }
  },

  /**
   * @private
   */
  startSaveGateRenew() {
    this.stopSaveGateRenew();

    this.state.renewTimer = window.setInterval(() => {
      this.renewSaveGate();
    }, this.SAVE_GATE_RENEW_MS);
  },

  /**
   * @private
   */
  stopSaveGateRenew() {
    const state = this.state;

    if (state.renewTimer) {
      window.clearInterval(state.renewTimer);
      state.renewTimer = null;
    }
  },

  /**
   * @private
   */
  renewSaveGate() {
    const state = this.state;

    if (!state.held) {
      this.stopSaveGateRenew();
      return;
    }

    const tabId = tabIdentity.peekTabId();

    if (!tabId) {
      return;
    }

    const existing = this.getSaveGate();

    if (existing && existing.tabId !== tabId) {
      state.held = false;
      this.stopSaveGateRenew();
      this.syncSaveGateFromSlot();
      return;
    }

    if (!this.writeSaveGateSlot(tabId)) {
      return;
    }

    sharedStorage.tabChannel.publish('save:renew', { tabId: tabId });
  },

  /**
   * @private
   *
   * @param {boolean} blocked
   */
  applySaveGatePeerBlock(blocked) {
    const $ctas = $(this.SAVE_GATE_CTA_SELECTOR);

    if (!$ctas.length) {
      return;
    }

    if (blocked) {
      $ctas.each((_, el) => {
        const $el = $(el);

        if ($el.hasClass('cxp-success')) {
          return;
        }

        $el.addClass('disabled').attr(this.SAVE_GATE_PEER_ATTR, '1');

        if ($el.is('button, input')) {
          $el.prop('disabled', true);
        }
      });
      return;
    }

    $ctas.filter('[' + this.SAVE_GATE_PEER_ATTR + '="1"]').each((_, el) => {
      const $el = $(el);

      $el.removeAttr(this.SAVE_GATE_PEER_ATTR);

      if ($el.hasClass('cxp-success')) {
        return;
      }

      $el.removeClass('disabled');

      if ($el.is('button, input')) {
        $el.prop('disabled', false);
      }
    });

    saveToCxpGate.reapplyNavCta();
  },

  /**
   * @private
   *
   * @param {JQuery} $ctas
   *
   * @returns {boolean}
   */
  isAnySaveGateCtaDisabled($ctas) {
    var disabled = false;

    $ctas.each((_, el) => {
      const $el = $(el);

      if ($el.hasClass('disabled') || ($el.is('button, input') && $el.prop('disabled'))) {
        disabled = true;

        return false;
      }

      return undefined;
    });

    return disabled;
  },

  /**
   * @private
   *
   * @param {JQuery} $ctas
   * @param {boolean} disabled
   */
  setLocalSaveGateCtaDisabled($ctas, disabled) {
    $ctas.each((_, el) => {
      const $el = $(el);

      if (disabled) {
        $el.addClass('disabled');

        if ($el.is('button, input')) {
          $el.prop('disabled', true);
        }

        return;
      }

      $el.removeClass('disabled');

      if ($el.is('button, input')) {
        $el.prop('disabled', false);
      }
    });
  },

  /**
   * @private
   *
   * @param {JQuery} $ctas
   */
  restoreLocalSaveGateCtas($ctas) {
    $ctas.each((_, el) => {
      const $el = $(el);

      if ($el.hasClass('cxp-success') || $el.attr(this.SAVE_GATE_PEER_ATTR)) {
        return;
      }

      this.setLocalSaveGateCtaDisabled($el, false);
    });

    saveToCxpGate.reapplyNavCta();
  }
};
